import axios from 'axios';
import { API_BASE_URL } from '../config';
import supabase from '../config/supabaseConfig';

const api = axios.create({ baseURL: API_BASE_URL });

// Request interceptor to set token
api.interceptors.request.use((config) => {

  const accessToken = localStorage.getItem("token");
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// Response interceptor to refresh if token expired
api.interceptors.response.use(
  res => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        // Use Supabase to refresh the session
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) throw error;
        
        if (data.session) {
          // Update local storage with new tokens
          localStorage.setItem("token", data.session.access_token);
          localStorage.setItem("refresh_token", data.session.refresh_token);
          
          // Calculate and store the expiry time
          const expiresIn = 3600; // Default to 1 hour if not provided
          const expiry = Date.now() + expiresIn * 1000;
          localStorage.setItem("expiry", String(expiry));
          
          // Update authorization header with new token
          original.headers.Authorization = `Bearer ${data.session.access_token}`;
          
          // Retry the original request
          return api(original);
        } else {
          throw new Error("No session returned from refresh");
        }
      } catch (refreshError) {
        console.error("Failed to refresh token:", refreshError);
        
        // Sign out from Supabase
        await supabase.auth.signOut();
        
        // Clear local storage
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        localStorage.removeItem("expiry");
        
        // Redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Proactive token refresh function
const startTokenAutoRefresh = () => {
  // First check immediately, then every minute after
  checkAndRefreshToken();
  
  return setInterval(() => {
    checkAndRefreshToken();
  }, 60_000); // Check every minute
};

// Function to check token expiry and refresh if needed
const checkAndRefreshToken = async () => {
  try {
    const expiry = Number(localStorage.getItem("expiry") || 0);
    const now = Date.now();

    // If token will expire in the next 60 seconds
    if (now > expiry - 60_000) {
      console.log("Token expiring soon, refreshing...");
      const refreshToken = localStorage.getItem("refresh_token");
      
      if (refreshToken) {
        // Use Supabase to refresh the session
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
          throw error;
        }
        
        if (data.session) {
          // Update local storage with new tokens and expiry
          localStorage.setItem("token", data.session.access_token);
          localStorage.setItem("refresh_token", data.session.refresh_token);
          
          // Calculate and store the new expiry time (default to 1 hour if not provided)
          const expiresIn = 3600;
          const newExpiry = Date.now() + expiresIn * 1000;
          localStorage.setItem("expiry", String(newExpiry));
          
          console.log("Token refreshed proactively");
        }
      }
    }
  } catch (error) {
    console.error("Error in token refresh:", error);
    
    // Handle refresh failure - sign out
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error("Error signing out:", e);
    }
    
    // Clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("expiry");
    
    // Redirect to login
    window.location.href = '/login';
  }
};

// Start the token auto-refresh if in browser environment
if (typeof window !== 'undefined') {
  startTokenAutoRefresh();
}

export default api;
